import {
  breakpoints,
  keyCodes,
  isAEMAuthorMode,
  scrollToComponent,
  subscribeToResize,
  updateAccordionIconsLabels,
  initDidomiKeyboardSupport,
} from '@msc-b2c/shared-utils';

(function () {
  'use strict';

  interface ContainerUtils {
    getDeepLinkItemIdx: (accordion: Accordion, itemSelector: string, itemElement: string) => number;
    updateUrlHash: (accordion: Accordion, itemSelector: string, index: number) => void;
    removeUrlHash: () => void;
    scrollToAnchor: () => void;
  }

  interface Window {
    CQ?: {
      CoreComponents?: {
        container?: {
          utils?: ContainerUtils;
        };
        MESSAGE_CHANNEL?: {
          subscribeRequestMessage: (channel: string, callback: (message: any) => void) => void;
        };
      };
    };
    Granite?: {
      author?: {
        MessageChannel?: any;
      };
    };
    didomiOnReady?: any[];
    didomiState?: {
      didomiVendorsEnabled?: any;
    };
  }

  const containerUtils: ContainerUtils | undefined =
    window.CQ?.CoreComponents?.container?.utils || undefined;

  if (!containerUtils) {
    console.warn(
      'Accordion: container utilities at window.CQ.CoreComponents.container.utils are not available. This can lead to missing features. Ensure the core.wcm.components.commons.site.container client library is included on the page.'
    );
  }

  let dataLayerEnabled: boolean;
  let dataLayer;
  let dataLayerName: string;
  const delay = 100;

  const NS = 'cmp';
  const IS = 'accordion';

  const selectors = {
    self: `[data-${NS}-is="${IS}"]`,
  };

  const cssClasses = {
    item: {
      expanded: 'cmp-accordion__item--expanded',
    },
    button: {
      disabled: 'cmp-accordion__button--disabled',
      expanded: 'cmp-accordion__button--expanded',
    },
    icon: {
      expanded: 'cmp-accordion__icon--expanded',
    },
    panel: {
      hidden: 'cmp-accordion__panel--hidden',
      expanded: 'cmp-accordion__panel--expanded',
    },
  };

  const dataAttributes = {
    item: {
      expanded: 'data-cmp-expanded',
    },
  };

  interface AccordionProperties {
    singleExpansion: {
      default: boolean;
      transform: (value: string) => boolean;
    };
    neverCollapse: {
      default: boolean;
      transform: (value: string) => boolean;
    };
  }

  const properties: AccordionProperties = {
    singleExpansion: {
      default: false,
      transform: function (value): boolean {
        return !(value === null || typeof value === 'undefined');
      },
    },
    neverCollapse: {
      default: false,
      transform: (value): boolean => {
        return value.toLowerCase() === 'true';
      },
    },
  };

  interface AccordionConfig {
    element: HTMLElement;
    options?: { [key: string]: undefined | string };
  }

  interface AccordionElements {
    self: HTMLElement;
    item?: HTMLElement | HTMLElement[];
    button?: HTMLElement | HTMLElement[];
    panel?: HTMLElement | HTMLElement[];

    [key: string]: HTMLElement | HTMLElement[] | undefined;
  }

  class Accordion {
    private _properties!: { [key: string]: string | boolean | number };
    private _elements!: AccordionElements;
    private isMobile(): boolean {
      return window.innerWidth < breakpoints.lg;
    }

    constructor(config: AccordionConfig) {
      if (config?.element) {
        this.init(config);
      }
    }

    private init(config: AccordionConfig): void {
      // prevents multiple initialization
      config.element.removeAttribute(`data-${NS}-is`);

      this.setupProperties(config.options);
      this.cacheElements(config.element);

      if (this._elements['item']) {
        // ensures multiple element types are arrays.
        this._elements['item'] = Array.isArray(this._elements['item'])
          ? this._elements['item']
          : [this._elements['item']];
        this._elements['button'] = Array.isArray(this._elements['button'])
          ? this._elements['button']
          : [this._elements['button'] as HTMLElement];
        this._elements['panel'] = Array.isArray(this._elements['panel'])
          ? this._elements['panel']
          : [this._elements['panel'] as HTMLElement];

        if (this._properties['singleExpansion']) {
          const expandedItems = this.getExpandedItems();
          // multiple expanded items annotated, display the last item open.
          if (expandedItems.length > 1) {
            this.toggle(expandedItems.length - 1);
          }
        }

        updateAccordionIconsLabels(true);
        this.refreshItems();
        this.bindEvents();
        this.scrollToDeepLinkIdInAccordion();
        this.anchorManagement();
        window.addEventListener('hashchange', () => {
          this.anchorManagement();
        });

        if (
          this._elements.self.parentElement?.classList.contains('cmp-interactive-visual-list-2')
        ) {
          this.handleInteractiveVisualList2();
        } else if (
          this._elements.self.parentElement?.classList.contains('cmp-interactive-visual-list-3')
        ) {
          this.handleInteractiveVisualList3();
        }
      }

      if (window.Granite?.author?.MessageChannel) {
        /**
         * Editor message handling:
         * - subscribe to "cmp.panelcontainer" message requests sent by the editor frame
         * - check that the message data panel container type is correct and that the id (path) matches this specific Accordion component
         * - if so, route the "navigate" operation to enact a navigation of the Accordion based on index data
         */
        window.CQ = window.CQ || {};
        window.CQ.CoreComponents = window.CQ.CoreComponents || {};
        window.CQ.CoreComponents.MESSAGE_CHANNEL =
          window.CQ.CoreComponents.MESSAGE_CHANNEL ||
          new window.Granite.author.MessageChannel('cqauthor', window);

        window.CQ.CoreComponents.MESSAGE_CHANNEL.subscribeRequestMessage(
          'cmp.panelcontainer',
          (message: {
            data: { type: string; id: string | undefined; operation: string; index: number };
          }) => {
            if (
              message.data &&
              message.data.type === 'cmp-accordion' &&
              message.data.id === this._elements.self.dataset['cmpPanelcontainerId']
            ) {
              if (message.data.operation === 'navigate') {
                // switch to single expansion mode when navigating in edit mode.
                const singleExpansion = this._properties['singleExpansion'];
                this._properties['singleExpansion'] = true;
                this.toggle(message.data.index);

                // revert to the configured state.
                this._properties['singleExpansion'] = singleExpansion;
              }
            }
          }
        );
      }
    }

    private updateTooltip(item: HTMLElement): void {
      const accordionButton = item.querySelector('.cmp-accordion__button');
      if (!accordionButton) return;
      const titleContainer = item.querySelector('.cmp-accordion__title_container');
      if (!titleContainer) return;
      const icon = titleContainer.querySelector('.cmp-accordion__icon');
      if (!icon) return;

      const openLabel = item.getAttribute('data-open-label') ?? '';
      const closeLabel = item.getAttribute('data-close-label') ?? '';
      if (this.getItemExpanded(item)) {
        icon.setAttribute('title', closeLabel);
      } else {
        icon.setAttribute('title', openLabel);
      }
    }

    private scrollToDeepLinkIdInAccordion(): void {
      if (containerUtils) {
        const deepLinkItemIdx = containerUtils.getDeepLinkItemIdx(this, 'item', 'item');
        if (deepLinkItemIdx > -1) {
          const deepLinkItem = (this._elements['item'] as HTMLElement[])[deepLinkItemIdx];
          if (deepLinkItem && !deepLinkItem.hasAttribute(dataAttributes.item.expanded)) {
            // if single expansion: close all accordion items
            if (this._properties['singleExpansion']) {
              (this._elements['item'] as HTMLElement[]).forEach((item) => {
                if (item.hasAttribute(dataAttributes.item.expanded)) {
                  this.setItemExpanded(item, false, true);
                }
              });
            }
            // expand the accordion item containing the deep link
            this.setItemExpanded(deepLinkItem, true, true);
          }
          const hashId = window.location.hash.substring(1);
          if (hashId) {
            const hashItem = document.querySelector(`[id='${hashId}']`);
            const serviceCardContainer = hashItem?.closest('.service-card');
            if (serviceCardContainer) {
              serviceCardContainer.scrollIntoView();
            } else if (hashItem) {
              hashItem.scrollIntoView();
            }
          }
        }
      }
    }

    private cacheElements(wrapper: HTMLElement): void {
      this._elements = {
        self: wrapper,
      };
      const hooks = this._elements.self.querySelectorAll(`[data-${NS}-hook-${IS}]`);

      hooks.forEach((hook) => {
        if (hook.closest(`.${NS}-${IS}`) === this._elements.self) {
          // only process own accordion elements
          const capitalized = IS.charAt(0).toUpperCase() + IS.slice(1);
          const key = (hook as HTMLElement).dataset[`${NS}Hook${capitalized}`] as string;
          if (this._elements[key]) {
            this._elements[key] = Array.isArray(this._elements[key])
              ? [...this._elements[key], hook as HTMLElement]
              : [this._elements[key] as HTMLElement, hook as HTMLElement];
          } else {
            this._elements[key] = hook as HTMLElement;
          }
        }
      });
    }

    private setupProperties(options: { [p: string]: string | undefined } | undefined): void {
      this._properties = {};

      for (const key in properties) {
        if (Object.prototype.hasOwnProperty.call(properties, key)) {
          const property = properties[key as keyof AccordionProperties];
          let value = null;

          if (options?.[key] != null) {
            value = options[key];

            // transform the provided option
            if (property && typeof property.transform === 'function') {
              value = property.transform(value);
            }
          }

          // value still null, take the property default
          value ??= property.default;

          this._properties[key] = value;
        }
      }
    }

    private bindEvents(): void {
      window.addEventListener('hashchange', this.scrollToDeepLinkIdInAccordion.bind(this), false);
      const buttons = this._elements['button'] as HTMLElement[];
      if (buttons) {
        for (let i = 0; i < buttons.length; i++) {
          ((index: number) => {
            buttons[index].addEventListener('click', () => {
              //if its ivl2 and you click on the expanded, dont collapse
              if (
                this._elements.self.parentElement?.classList.contains(
                  'cmp-interactive-visual-list-2'
                ) &&
                buttons[index].classList.contains('cmp-accordion__button--expanded')
              )
                return;
              this.toggle(index);
              this.focusButton(index);
              this.getAccordionParentClass(index)?.closest('.service-card')?.scrollIntoView({
                block: 'start',
              });
              if (
                this._elements.self.parentElement?.classList.contains(
                  'cmp-interactive-visual-list-2'
                )
              ) {
                requestAnimationFrame(() => {
                  const navHeight =
                    document.querySelector<HTMLElement>('.dynamicnavigationtabs')?.offsetHeight ??
                    88;
                  const container = ((this._elements.self as HTMLElement).parentElement
                    ?.parentElement ?? this._elements.self) as HTMLElement;
                  const scrollTarget =
                    container.querySelector<HTMLElement>('h1,h2,h3,h4') ?? container;
                  if (Math.abs(scrollTarget.getBoundingClientRect().top - navHeight) <= 5) return;
                  document.body.style.scrollPaddingTop = `${navHeight}px`;
                  scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
                });
              }
            });
            buttons[index].addEventListener('keydown', (event: KeyboardEvent) => {
              this.onButtonKeyDown(event, index);
            });
            buttons[index].addEventListener('touchstart', (event: TouchEvent) => {
              event.stopImmediatePropagation();
              this.onTouchEvent(event, index);
            });
          })(i);
        }
      }
    }

    private onTouchEvent(_event: TouchEvent, index: number) {
      this.handleAccordionButtonOutline(index, false);
    }

    private handleAccordionButtonOutline(index: number, remove: boolean) {
      const accordioElement = this.getAccordionParentClass(index);
      if (accordioElement) {
        if (remove) {
          accordioElement.classList.remove('-outline-mobile');
        } else {
          accordioElement.classList.add('-outline-mobile');
        }
      }
    }

    private getAccordionParentClass(index: number) {
      const item = (this._elements['item'] as HTMLElement[])[index];
      const accordioElement = item.parentElement?.parentElement;
      if (accordioElement) {
        return accordioElement;
      }

      return null;
    }

    private onButtonKeyDown(event: KeyboardEvent, index: number): void {
      const lastIndex = (this._elements['button'] as HTMLElement[]).length - 1;

      switch (event.key) {
        case keyCodes.ARROW_LEFT:
        case keyCodes.ARROW_UP:
          event.preventDefault();
          if (index > 0) {
            this.focusButton(index - 1);
          }
          break;
        case keyCodes.ARROW_RIGHT:
        case keyCodes.ARROW_DOWN:
          event.preventDefault();
          if (index < lastIndex) {
            this.focusButton(index + 1);
          }
          break;
        case keyCodes.HOME:
          event.preventDefault();
          this.focusButton(0);
          break;
        case keyCodes.END:
          event.preventDefault();
          this.focusButton(lastIndex);
          break;
        case keyCodes.ENTER:
        case keyCodes.SPACE:
          event.preventDefault();
          this.toggle(index);
          this.getAccordionParentClass(index)?.closest('.service-card')?.scrollIntoView({
            block: 'start',
          });
          this.focusButton(index);
          break;
        case keyCodes.TAB:
          this.handleAccordionButtonOutline(index, true);
          break;
        default:
          return;
      }
    }

    private toggle(index: number): void {
      const item = (this._elements['item'] as HTMLElement[])[index];
      if (!item) return;

      const isCurrentlyExpanded = this.getItemExpanded(item);

      if (this._properties['neverCollapse'] && isCurrentlyExpanded && this.isMobile()) {
        const expandedItems = this.getExpandedItems();
        if (expandedItems.length === 1 && expandedItems[0] === item) {
          return; // keep it open
        }
      }

      if (this._properties['singleExpansion']) {
        (this._elements['item'] as HTMLElement[]).forEach((el) => {
          if (el !== item && this.getItemExpanded(el)) {
            this.setItemExpanded(el, false);
            if (this.hasParentWithClass(el, 'cmp-interactive-visual-list')) {
              el?.querySelector('button')?.classList.remove('cmp-no-events');
            }
          }
        });
      }

      this.setItemExpanded(item, !isCurrentlyExpanded);

      if (dataLayerEnabled) {
        const accordionId = this._elements.self.id;
        const expandedItems = this.getExpandedItems().map((it) => this.getDataLayerId(it));

        interface ComponentPayload {
          [key: string]: { shownItems: (string | null)[] | undefined };
        }

        const uploadPayload = { component: {} as ComponentPayload };
        uploadPayload.component[accordionId] = { shownItems: expandedItems };

        const removePayload = { component: {} as ComponentPayload };
        removePayload.component[accordionId] = { shownItems: undefined };
        window.didomiOnReady = window.didomiOnReady || [];
        console.log(
          'Didomi Wrapper accordion dataLayer update called - didomiOnReady: ',
          window.didomiOnReady
        );
        window.didomiOnReady.push(() => {
          console.log('didomiVendorsEnabled =', window.didomiState?.didomiVendorsEnabled);
          console.log('Data to be pushed (removePayload):', removePayload);
          dataLayer.push(removePayload);
          console.log('Data to be pushed (uploadPayload):', uploadPayload);
          dataLayer.push(uploadPayload);
          console.log('Custom event accordion dataLayer update pushed into the data layer');
        });
      }
    }

    private setItemExpanded(item: HTMLElement, expanded: boolean, keepHash?: boolean): void {
      const serviceCardContainer = item.closest('.service-card');
      window.didomiOnReady = window.didomiOnReady || [];
      if (expanded) {
        if (serviceCardContainer) {
          serviceCardContainer.classList.add('accordion-open');
        }
        item.setAttribute(dataAttributes.item.expanded, '');
        if (dataLayerEnabled) {
          window.didomiOnReady = window.didomiOnReady || [];
          console.log('Didomi Wrapper accordion cmp:show called - About to push callback');
          window.didomiOnReady.push(() => {
            console.log('Didomi Wrapper accordion cmp:show called - Didomi is ready');
            console.log('didomiVendorsEnabled =', window.didomiState?.didomiVendorsEnabled);
            const showData = {
              event: 'cmp:show',
              eventInfo: {
                path: 'component.' + this.getDataLayerId(item),
              },
            };
            console.log('Data to be pushed:', showData);
            dataLayer.push(showData);
            console.log('Custom event accordion cmp:show pushed into the data layer');
          });
        }
      } else {
        if (serviceCardContainer) {
          serviceCardContainer.classList.remove('accordion-open');
        }
        item.removeAttribute(dataAttributes.item.expanded);
        if (!keepHash && containerUtils) {
          containerUtils.removeUrlHash();
        }
        if (dataLayerEnabled) {
          window.didomiOnReady = window.didomiOnReady || [];
          console.log('Didomi Wrapper accordion cmp:hide called - About to push callback');
          window.didomiOnReady.push(() => {
            console.log('Didomi Wrapper accordion cmp:hide called - Didomi is ready');
            console.log('didomiVendorsEnabled =', window.didomiState?.didomiVendorsEnabled);
            const hideData = {
              event: 'cmp:hide',
              eventInfo: {
                path: 'component.' + this.getDataLayerId(item),
              },
            };
            console.log('Data to be pushed:', hideData);
            dataLayer.push(hideData);
            console.log('Custom event accordion cmp:hide pushed into the data layer');
          });
        }
      }
      this.refreshItem(item);
    }

    private getItemExpanded(item: HTMLElement): boolean {
      return item?.dataset?.['cmpExpanded'] !== undefined;
    }

    private refreshItem(item: HTMLElement): void {
      const expanded = this.getItemExpanded(item);
      if (expanded) {
        this.expandItem(item);
        this.updateTooltip(item);
      } else {
        this.collapseItem(item);
        this.updateTooltip(item);
      }
    }

    private refreshItems(): void {
      (this._elements['item'] as HTMLElement[]).forEach((item) => this.refreshItem(item));
    }

    private anchorManagement() {
      const urlHash = window.location.hash;
      if (!urlHash) return;
      const accordionPanel = document
        .getElementById(urlHash.replace('#', ''))
        ?.closest('.cmp-accordion__panel');
      if (!accordionPanel) return;
      const isIvl3 = accordionPanel?.closest('.cmp-interactive-visual-list-3') ? true : false;
      if (isIvl3) {
        const accordionHeader = (accordionPanel as HTMLElement).parentElement?.querySelector(
          '.cmp-accordion__header'
        );
        if (!accordionHeader) return;
        const accordionButton = accordionHeader.querySelector('.cmp-accordion__button');
        if (!accordionButton) return;
        if (accordionButton.classList.contains('cmp-accordion__button--expanded')) return;
        (accordionButton as HTMLElement).click();
      }
    }

    private getExpandedItems(): HTMLElement[] {
      return (this._elements['item'] as HTMLElement[]).filter((item) => this.getItemExpanded(item));
    }

    private hasParentWithClass(element: HTMLElement, parentClass: string): boolean {
      let parent = element.parentElement;
      while (parent) {
        if (parent.classList.contains(parentClass)) {
          return true;
        }
        parent = parent.parentElement;
      }
      return false;
    }

    private expandItem(item: HTMLElement): void {
      const index = (this._elements['item'] as HTMLElement[]).indexOf(item);
      if (index > -1) {
        const button = (this._elements['button'] as HTMLElement[])[index];
        const panel = (this._elements['panel'] as HTMLElement[])[index];
        button.classList.add(cssClasses.button.expanded);
        const isInteractiveVisual = this.hasParentWithClass(button, 'cmp-interactive-visual-list');
        if (isInteractiveVisual) {
          button.classList.add('cmp-no-events');
        }
        // used to fix some known screen readers issues in reading the correct state of the 'aria-expanded' attribute
        // e.g. https://bugs.webkit.org/show_bug.cgi?id=210934
        setTimeout(() => {
          button.setAttribute('aria-expanded', 'true');
        }, delay);
        panel.classList.add(cssClasses.panel.expanded);
        panel.classList.remove(cssClasses.panel.hidden);
        panel.setAttribute('aria-hidden', 'false');
        this.setTabIndexOnFocusElements(panel, false);
        ///
        item.classList.add(cssClasses.item.expanded); // accordion padding

        const isDesktop = window.innerWidth >= breakpoints.lg;
        if (isInteractiveVisual && isDesktop) {
          button.classList.add('cmp-no-events');
        } else {
          button.classList.remove('cmp-no-events');
        }

        const icon = button.querySelector('.cmp-accordion__icon') as HTMLElement;
        icon.classList.remove(cssClasses.icon.expanded);
        const collapsedIconClassName = icon.getAttribute('data-opening-icon');
        const expandedIconClassName = icon.getAttribute('data-closing-icon');
        if (collapsedIconClassName && expandedIconClassName) {
          if (
            !icon.classList.contains(`icon-${expandedIconClassName}`) &&
            !icon.classList.contains(`icon-${collapsedIconClassName}`)
          ) {
            icon.classList.add(`icon-${expandedIconClassName}`);
          } else {
            icon.classList.replace(
              `icon-${collapsedIconClassName}`,
              `icon-${expandedIconClassName}`
            );
          }
        }
      }
    }

    private handleInteractiveVisualList2(): void {
      const items = this._elements.item as HTMLElement[];
      if (items.length > 0) {
        if (!items[0].classList.contains(cssClasses.item.expanded)) this.toggle(0);
      }
      document.querySelectorAll('.rt-content-fragment').forEach((span) => {
        span.addEventListener('click', (event) => {
          const link = (event.currentTarget as HTMLElement).querySelector('a');
          if (link) {
            window.location.href = link.href;
          }
        });
      });
      let minHeigh = 0;
      const itemDistance = (): void => {
        items.forEach((item, index) => {
          const header = item.querySelector('.cmp-accordion__header') as HTMLElement;
          const distanceFromPrevious = 101.5 * index;
          header.style.top = `${distanceFromPrevious}px`;
          minHeigh = distanceFromPrevious + header.offsetHeight;
        });
        if (items.length > 0) {
          const divContainer = items[0].closest('.cmp-accordion') as HTMLDivElement;
          divContainer.style.minHeight = `${minHeigh}px`;
        }
      };
      const mobileFunctionality = (): void => {
        if (!items) return;
        items.forEach((item, index) => {
          const button = item.querySelector('.cmp-accordion__button');
          if (button) {
            button.classList.add('cmp-no-events');
          }
          const nextIcon = item.querySelector('.icon-navigation-right');
          const nextButton = nextIcon?.parentElement;

          const prevIcon = item.querySelector('.icon-navigation-left');
          const prevButton = prevIcon?.parentElement;

          if (prevButton) {
            if (index === 0) {
              prevButton.classList.add('disabled');
              prevButton.setAttribute('disabled', 'true');
            } else {
              prevButton.classList.remove('disabled');
              prevButton.removeAttribute('disabled');
            }

            prevButton.addEventListener('click', (e: Event) => {
              e.stopImmediatePropagation();
              if (index > 0) {
                this.toggle(index - 1);
              }
            });
          }

          if (nextButton) {
            if (index === items.length - 1) {
              nextButton.classList.add('disabled');
              nextButton.setAttribute('disabled', 'true');
            } else {
              nextButton.classList.remove('disabled');
              nextButton.removeAttribute('disabled');
            }

            nextButton.addEventListener('click', (e: Event) => {
              e.stopImmediatePropagation();
              if (index < items.length - 1) {
                this.toggle(index + 1);
              }
            });
          }
        });
      };
      itemDistance();
      if (window.innerWidth < breakpoints.lg) {
        mobileFunctionality();
      } else {
        const cleanup = subscribeToResize(({ width }) => {
          if (!(this._elements.self as HTMLElement).isConnected) {
            cleanup();
            return;
          }
          if (width < breakpoints.lg) mobileFunctionality();
        });
      }
    }

    private handleInteractiveVisualList3(): void {
      const items = this._elements.item as HTMLElement[];

      items.forEach((item) => {
        const header = item.querySelector('.cmp-accordion__header') as HTMLElement;
        if (window.innerWidth < breakpoints.lg) {
          item.addEventListener('click', (e: Event) => {
            if (item.classList.contains(cssClasses.item.expanded)) {
              scrollToComponent(e.target as HTMLElement);
            }
          });
        } else {
          header.addEventListener('click', (e: Event) => {
            e.stopPropagation();
            if (item.classList.contains(cssClasses.item.expanded)) {
              scrollToComponent(this._elements.self);
            }
          });
          item.addEventListener('click', (e: Event) => {
            if (item.classList.contains(cssClasses.item.expanded)) {
              scrollToComponent(e.target as HTMLElement);
            }
          });
        }
      });
    }

    // make elements inside accordion panel focusable only if accordion is open
    private setTabIndexOnFocusElements(panel: HTMLElement, isExpanded: boolean) {
      const focusables = panel.querySelectorAll<HTMLElement>('a, button, [tabindex]');
      if (focusables && focusables.length > 0) {
        if (isExpanded) {
          focusables.forEach((node) => {
            node.tabIndex = -1;
          });
        } else {
          focusables.forEach((node) => {
            node.tabIndex = 0;
          });
        }
      }
    }

    private collapseItem(item: HTMLElement): void {
      const index = (this._elements['item'] as HTMLElement[]).indexOf(item);
      if (index > -1) {
        const button = (this._elements['button'] as HTMLElement[])[index];
        const panel = (this._elements['panel'] as HTMLElement[])[index];
        button.classList.remove(cssClasses.button.expanded);
        // used to fix some known screen readers issues in reading the correct state of the 'aria-expanded' attribute
        // e.g. https://bugs.webkit.org/show_bug.cgi?id=210934
        setTimeout(() => {
          button.setAttribute('aria-expanded', 'false');
        }, delay);
        panel.classList.add(cssClasses.panel.hidden);
        panel.classList.remove(cssClasses.panel.expanded);
        panel.setAttribute('aria-hidden', 'true');
        this.setTabIndexOnFocusElements(panel, true);
        ///
        item.classList.remove(cssClasses.item.expanded); // accordion padding
        const icon = button.querySelector('.cmp-accordion__icon') as HTMLElement;
        icon.classList.add(cssClasses.icon.expanded);
        const collapsedIconClassName = icon.getAttribute('data-opening-icon');
        const expandedIconClassName = icon.getAttribute('data-closing-icon');
        if (collapsedIconClassName && expandedIconClassName) {
          if (
            !icon.classList.contains(`icon-${collapsedIconClassName}`) &&
            !icon.classList.contains(`icon-${expandedIconClassName}`)
          ) {
            icon.classList.add(`icon-${collapsedIconClassName}`);
          } else {
            icon.classList.replace(
              `icon-${expandedIconClassName}`,
              `icon-${collapsedIconClassName}`
            );
          }
        }
      }
    }

    private focusButton(index: number): void {
      const button = (this._elements['button'] as HTMLElement[])[index];
      button.focus();
    }

    private getDataLayerId(item: HTMLElement): string | null {
      if (item) {
        if (item.dataset['cmpDataLayer']) {
          return Object.keys(JSON.parse(item.dataset['cmpDataLayer']))[0];
        } else {
          return item.id;
        }
      }
      return null;
    }
  }

  function readData(element: HTMLElement) {
    const data = element.dataset;
    const options: { [key: string]: undefined | string } = {};
    let capitalized = IS;
    capitalized = capitalized.charAt(0).toUpperCase() + capitalized.slice(1);
    const reserved = ['is', `hook${capitalized}`];

    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        const value = data[key];

        if (key.startsWith(NS)) {
          let newKey = key.slice(NS.length);
          newKey = newKey.charAt(0).toLowerCase() + newKey.substring(1);

          if (reserved.indexOf(newKey) === -1) {
            options[newKey] = value;
          }
        }
      }
    }

    return options;
  }

  function onDocumentReady(): void {
    initDidomiKeyboardSupport();

    dataLayerEnabled = document.body.hasAttribute('data-cmp-data-layer-enabled');
    if (dataLayerEnabled) {
      dataLayerName = document.body.getAttribute('data-cmp-data-layer-name') ?? 'adobeDataLayer';
      dataLayer = window[dataLayerName] = window[dataLayerName] || [];
    }

    const elements = document.querySelectorAll(selectors.self);
    elements.forEach((element) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const _accordion = new Accordion({
        element: element as HTMLElement,
        options: readData(element as HTMLElement),
      });
    });

    document
      .querySelectorAll('.accessibilityformcontainer .cmp-accordion > div')
      .forEach((el) => el.classList.add('cmp-accordion__skeleton'));

    if (!isAEMAuthorMode()) {
      const MutationObserver =
        window.MutationObserver || window.WebKitMutationObserver || window.MozMutationObserver;
      const body = document.querySelector('body');
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          // needed for IE
          const nodesArray = Array.from(mutation.addedNodes);
          if (nodesArray.length > 0) {
            nodesArray.forEach((addedNode) => {
              if ((addedNode as Element).querySelectorAll) {
                const elementsArray = Array.from(
                  (addedNode as Element).querySelectorAll(selectors.self)
                );
                elementsArray.forEach((element) => {
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  const _accordion = new Accordion({
                    element: element as HTMLElement,
                    options: readData(element as HTMLElement),
                  });
                });
              }
            });
          }
        });
      });

      if (body) {
        observer.observe(body, {
          subtree: true,
          childList: true,
          characterData: true,
        });
      }
    }

    showHideAccordionElements();
    setupShowMoreLessButtons();
  }

  function showHideAccordionElements(): void {
    const maxNumber = getMaxNumberFromFirstHeader();
    let headers = document.querySelectorAll<HTMLHeadingElement>('div.cmp-accordion__item');
    if (headers.length === 0) {
      headers = document.querySelectorAll<HTMLDivElement>('div.accordion__cmp');
    }
    const childrenNumber = headers.length;

    const container = document.querySelector('.showmoreshowless') as HTMLElement;
    if (maxNumber == 0 || maxNumber == null) {
      if (container) container.style.display = 'none';
    } else if (maxNumber && childrenNumber && childrenNumber <= maxNumber) {
      if (container) container.style.display = 'none';
    }

    if (maxNumber && childrenNumber > 0 && childrenNumber > maxNumber) {
      const excess = childrenNumber - maxNumber;
      const headersToHide = Array.from(headers).slice(-excess);

      headersToHide.forEach((header) => {
        header.style.display = 'none';
      });
    }
  }

  function getMaxNumberFromFirstHeader(): number | null {
    let header = document.querySelector<HTMLHeadingElement>('h3.cmp-accordion__header');
    header ??= document.querySelector<HTMLDivElement>('div.accordion__cmp');

    const attr = header?.getAttribute('maxnumber');

    if (attr !== null) {
      const num = Number(attr);
      return isNaN(num) ? null : num;
    }
    return null;
  }

  function setupShowMoreLessButtons(): void {
    const showMoreBtn = document.getElementById('showmore-voyagersclub-faq');
    const showLessBtn = document.getElementById('showless-voyagersclub-faq');
    if (!showMoreBtn || !showLessBtn) return;

    const initiallyHiddenHeaders: HTMLElement[] = [];

    const headers = document.querySelectorAll<HTMLElement>('div.cmp-accordion__item');
    headers.forEach((header) => {
      if (getComputedStyle(header).display === 'none') {
        initiallyHiddenHeaders.push(header);
      }
    });

    // Initial state
    if (initiallyHiddenHeaders.length > 0) {
      showMoreBtn.style.display = 'inline-flex';
      showLessBtn.style.display = 'none';
    } else {
      showMoreBtn.style.display = 'none';
      showLessBtn.style.display = 'none';
    }

    showMoreBtn.addEventListener('click', () => {
      initiallyHiddenHeaders.forEach((header) => {
        header.style.display = 'block';
      });
      showMoreBtn.style.display = 'none';
      showLessBtn.style.display = 'inline-flex';
    });

    showLessBtn.addEventListener('click', () => {
      initiallyHiddenHeaders.forEach((header) => {
        header.style.display = 'none';
      });
      showMoreBtn.style.display = 'inline-flex';
      showLessBtn.style.display = 'none';
    });
  }

  if (document.readyState !== 'loading') {
    onDocumentReady();
  } else {
    document.addEventListener('DOMContentLoaded', onDocumentReady);
  }

  if (containerUtils) {
    window.addEventListener('load', containerUtils.scrollToAnchor, false);
  }
})();
